import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import SettingsRecommendationsDynamicRateRangesTdDiscount from 'lumen/components/settings/recommendations/dynamic-rate-ranges/td-discount';
import type { ConditionalStrategy, RecommendationStrategy } from 'lumen/models';
import type {
  DynamicRateDiscount,
  DynamicRateDiscountRangeSettings,
  LeadtimeConditionSummary,
} from 'lumen/models/recommendation-strategy';
import { PRICE_RANGE_STRATEGY_CONDITION_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { formatLeadtimeConditionLabel } from 'lumen/utils/pricing-assistant/format-leadtime-condition';
import { sortDynamicRateRangeRows } from 'lumen/utils/pricing-assistant/sort-dynamic-rate-range-rows';
import { toStoredValue } from 'lumen/utils/pricing-assistant/value-conversion';

import styles from './dynamic-rate-ranges-strategy-table.module.css';

type DynamicRateRangesRow = {
  className?: string;
  isDefault: boolean;
  leadtimeLabel: string;
  maximum: DynamicRateDiscount | null;
  minimum: DynamicRateDiscount | null;
  // The raw rate code (used for write-back and per-cell test ids)
  rate: string;
  // What the Rate column shows: the user defined rate code names, or the raw
  // code as fallback.
  rateName: string;
  // The strategy an edit to this row should write back to: the default
  // strategy itself, or the specific conditional strategy for a leadtime band.
  targetStrategy: ConditionalStrategy | RecommendationStrategy;
};

interface SettingsRecommendationsDynamicRateRangesStrategyTableSignature {
  Args: {
    isEditMode: boolean;
    saveAttempted: boolean;
    strategy: RecommendationStrategy;
  };
}

export default class SettingsRecommendationsDynamicRateRangesStrategyTable extends Component<SettingsRecommendationsDynamicRateRangesStrategyTableSignature> {
  @service declare intl: Services['intl'];

  onDiscountChange = (
    row: Pick<DynamicRateRangesRow, 'rate' | 'targetStrategy'>,
    setting: 'maximum' | 'minimum',
    value: number | null,
  ): void => {
    const { rate, targetStrategy } = row;

    if (
      targetStrategy.settings.settings_type !== 'dynamic_rate_discount_range'
    ) {
      return;
    }

    const { settings } = targetStrategy;

    // Preserve the discount type the BE sent; newly-set fields default to relative
    // (the only type supported for now). Relative percentages are stored as fractions.
    const type = settings.rate_discounts[rate]?.[setting]?.type ?? 'relative';

    const discount: DynamicRateDiscount | null =
      value === null
        ? null
        : {
            type,
            value: toStoredValue(value, type),
          };

    targetStrategy.settings = {
      ...settings,
      rate_discounts: {
        ...settings.rate_discounts,
        [rate]: {
          ...(settings.rate_discounts[rate] ?? {
            maximum: null,
            minimum: null,
          }),
          [setting]: discount,
        },
      },
    };
  };

  get columns(): CustomEmberTableColumn[] {
    const { isEditMode, saveAttempted } = this.args;

    return [
      {
        hasRightBorder: true,
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.rate',
        ),
        thClassNames: 'text-left',
        valuePath: 'rateName',
      },
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.leadtime',
        ),
        thClassNames: 'text-left',
        valuePath: 'leadtimeLabel',
      },
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.minimum',
        ),
        onDiscountChange: this.onDiscountChange,
        saveAttempted,
        tdComponent: SettingsRecommendationsDynamicRateRangesTdDiscount,
        thClassNames: 'text-left',
        valuePath: 'minimum',
      },
      {
        isEditing: isEditMode,
        name: this.intl.t(
          'pricing_assistant.recommendations.settings.dynamic_rate_ranges.table.maximum',
        ),
        onDiscountChange: this.onDiscountChange,
        saveAttempted,
        tdComponent: SettingsRecommendationsDynamicRateRangesTdDiscount,
        thClassNames: 'text-left',
        valuePath: 'maximum',
      },
    ];
  }

  /* One base "Default" row per rate, in the default strategy's response
   * order.
   */
  private get defaultRows(): DynamicRateRangesRow[] {
    const { strategy: defaultStrategy } = this.args;
    const { settings } = defaultStrategy;

    if (settings.settings_type !== 'dynamic_rate_discount_range') {
      return [];
    }

    const defaultLabel = this.intl.t('general.default');

    return Object.entries(settings.rate_discounts ?? {}).map(
      ([rate, { maximum, minimum, user_defined_rate_code_name }]) => ({
        isDefault: true,
        leadtimeLabel: defaultLabel,
        maximum,
        minimum,
        rate,
        rateName: user_defined_rate_code_name ?? rate,
        targetStrategy: defaultStrategy,
      }),
    );
  }

  /* One row per rate for each conditional strategy with a leadtime band,
   * sorted by the band's lower bound (a band open below, e.g. "< 3", sorts
   * first). Conditional strategies without a leadtime condition_summary
   * (e.g. OTB-based) are skipped.
   */
  private get leadtimeConditionalRows(): DynamicRateRangesRow[] {
    const { strategy: defaultStrategy } = this.args;

    const bands = defaultStrategy.conditionalStrategies
      .filter(
        (
          conditionalStrategy,
        ): conditionalStrategy is ConditionalStrategy & {
          conditionSummary: LeadtimeConditionSummary;
          settings: DynamicRateDiscountRangeSettings;
        } => {
          const { conditionSummary, settings } = conditionalStrategy;

          return (
            conditionSummary?.name ===
              PRICE_RANGE_STRATEGY_CONDITION_TYPES.LEADTIME &&
            (conditionSummary.lowerBound?.value !== undefined ||
              conditionSummary.upperBound?.value !== undefined) &&
            settings.settings_type === 'dynamic_rate_discount_range'
          );
        },
      )
      .map((conditionalStrategy) => ({
        conditionalStrategy,
        settings: conditionalStrategy.settings,
        summary: conditionalStrategy.conditionSummary,
      }))
      .sort(
        (a, b) =>
          (a.summary.lowerBound?.value ?? -Infinity) -
          (b.summary.lowerBound?.value ?? -Infinity),
      );

    return bands.flatMap(({ conditionalStrategy, settings, summary }) => {
      const label = formatLeadtimeConditionLabel(summary);

      return Object.entries(settings.rate_discounts).map(
        ([rate, { maximum, minimum }]) => ({
          // A rate can't exist only via a conditional strategy, so every row
          // here has a corresponding default row - blank rateName and mark
          // "rate hidden" so the Rate cell reads as a single cell spanning
          // the group (divider/hover suppressed via the CSS module).
          className: 'dynamic-rate-ranges-row--rate-hidden',
          isDefault: false,
          leadtimeLabel: label,
          maximum,
          minimum,
          rate,
          rateName: '',
          targetStrategy: conditionalStrategy,
        }),
      );
    });
  }

  get rows(): DynamicRateRangesRow[] {
    return sortDynamicRateRangeRows(
      this.defaultRows,
      this.leadtimeConditionalRows,
    );
  }

  <template>
    <div class={{styles.table}}>
      <PrismTable @columns={{this.columns}} @rows={{this.rows}} />
    </div>
  </template>
}
